import { motion } from "framer-motion";
import { LocaleLink } from "@/components/LocaleLink";
import { useT } from "@/lib/i18n";
import { COLOR_CATEGORIES } from "@/lib/colors";
import { SITE_URL } from "@/lib/seo";

export const colorsIndexHeadDa = () => ({
  meta: [
    { title: "Shop efter farve — Farveladeland" },
    {
      name: "description",
      content:
        "Find vintage Murano-lamper, glas og loppefund i din yndlingsfarve. Lyserød, gul, grøn, blå, rød, orange, lilla og hvid — alt mundblæst og hånd-plukket.",
    },
    { property: "og:title", content: "Shop efter farve — Farveladeland" },
    {
      property: "og:description",
      content: "Otte farver, ét land. Vælg en tone og se vores fund i netop den.",
    },
    { rel: "canonical", href: `${SITE_URL}/farver` } as never,
  ],
});

export const colorsIndexHeadEn = () => ({
  meta: [
    { title: "Shop by colour — Farveladeland" },
    {
      name: "description",
      content:
        "Find vintage Murano lamps, glassware and flea-market finds in your favourite colour. Pink, yellow, green, blue, red, orange, purple and white — all hand-blown, hand-picked.",
    },
    { property: "og:title", content: "Shop by colour — Farveladeland" },
    {
      property: "og:description",
      content: "Eight colours, one land. Pick a tone and see our finds in it.",
    },
    { rel: "canonical", href: `${SITE_URL}/en/farver` } as never,
  ],
});

export function ColorsIndex() {
  const { t, locale } = useT();

  return (
    <>
      <section className="pt-40 pb-12 md:pt-52 md:pb-16">
        <div className="mx-auto max-w-[1500px] px-6 md:px-10">
          <p className="eyebrow">{t("colors.eyebrow")}</p>
          <h1 className="mt-4 font-display text-[clamp(3rem,7vw,6rem)] leading-[0.95]">
            {t("colors.titleA")}
            <br />
            <em className="text-terracotta">{t("colors.titleB")}</em>
          </h1>
          <p className="mt-6 max-w-xl text-pretty text-base leading-relaxed text-muted-foreground">
            {t("colors.body")}
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-[1500px] px-6 pb-32 md:px-10">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-4">
          {COLOR_CATEGORIES.map((c, i) => (
            <motion.div
              key={c.slug}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6, delay: i * 0.05, ease: [0.22, 1, 0.36, 1] }}
            >
              <LocaleLink
                to="/farver/$slug"
                params={{ slug: c.slug }}
                className={`group relative block aspect-[4/5] overflow-hidden rounded-sm transition-transform duration-500 ease-out hover:-translate-y-1 ${c.panel}`}
                aria-label={locale === "da" ? c.labelDa : c.labelEn}
              >
                {c.image ? (
                  <img
                    src={c.image}
                    alt=""
                    loading="lazy"
                    decoding="async"
                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
                  />
                ) : null}

                <div
                  aria-hidden
                  className="absolute inset-0"
                  style={{
                    background: c.image
                      ? "linear-gradient(180deg, transparent 40%, oklch(0.14 0.01 50 / 0.65) 100%)"
                      : "linear-gradient(180deg, transparent 60%, oklch(0.14 0.01 50 / 0.20) 100%)",
                  }}
                />

                <div className="absolute inset-x-0 bottom-0 flex items-end gap-3 p-5 md:p-6">
                  <span
                    className="block h-5 w-5 shrink-0 rounded-full ring-1 ring-white/70"
                    style={{ backgroundColor: c.swatch }}
                    aria-hidden
                  />
                  <div>
                    <h2
                      className={`font-display text-2xl leading-tight md:text-3xl ${
                        c.image ? "text-background" : "text-foreground"
                      }`}
                    >
                      {locale === "da" ? c.labelDa : c.labelEn}
                    </h2>
                    <p
                      className={`mt-1 line-clamp-2 max-w-[28ch] text-xs leading-snug md:text-sm ${
                        c.image ? "text-background/85" : "text-foreground/70"
                      }`}
                    >
                      {locale === "da" ? c.introDa : c.introEn}
                    </p>
                  </div>
                </div>
              </LocaleLink>
            </motion.div>
          ))}
        </div>
      </section>
    </>
  );
}
